import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Check, Truck } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import * as fbs from "@/api/fbs";
import { Button } from "@/components/ui/button";
import { ordersWord, piecesWord } from "@/lib/fbs";
import { apiErrorText, LoadState } from "@/lib/scan-ui/screen";

/**
 * Завоз в комнату ФБС: заказы на перемещение по юрлицам.
 *
 * Экран сознательно НЕ скан-экран. Собственного добора больше нет — портал
 * только формирует ЗАДАНИЕ складу (`Document_ЗаказНаПеремещение`), а выполняют
 * его в «Проверке перемещений» тем же сканом, что и все прочие перемещения.
 *
 * Потребность — это ПРОСТО количество несобранных заданий. Остаток комнаты не
 * вычитается: кладовщик сначала собирает всё, что в комнате есть, и только
 * потом заказывает перемещение — значит несобранное осталось именно потому,
 * что товара в комнате нет.
 */
export function FbsTransferOrdersPage(): React.ReactElement {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [created, setCreated] = useState<fbs.TransferOrderCreated[]>([]);

  const previewQ = useQuery({
    queryKey: ["fbs-transfer-preview"],
    queryFn: fbs.getTransferOrderPreview,
    // Потребность считается запросами в 1С, а между заходами на экран она
    // почти не меняется — пересчитывать на каждом монтировании незачем.
    staleTime: 60_000,
  });

  const createMut = useMutation({
    mutationFn: (keys: { cabinet_id: number; marketplace: string }[]) =>
      fbs.createTransferOrders(keys),
    onSuccess: (data) => {
      setCreated(data.created);
      // Пересчёт лениво: экран уже показывает итог, а дальше уходят в
      // «Проверку перемещений» — немедленный refetch ушёл бы в мусор.
      void qc.invalidateQueries({
        queryKey: ["fbs-transfer-preview"],
        refetchType: "none",
      });
    },
  });

  const preview = previewQ.data;
  const cabinets = (preview?.cabinets ?? []).filter((c) => c.total > 0);
  // ⚠️ Ключ — ПАРА: у ВБ и Ozon бывают кабинеты с одинаковым id.
  const keyOf = (c: { marketplace: string; cabinet_id: number }) =>
    `${c.marketplace}:${c.cabinet_id}`;
  const chosen = cabinets.filter((c) => !skipped.has(keyOf(c)));

  const toggle = (id: string) =>
    setSkipped((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="size-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold">Завоз в комнату ФБС</h1>
          <p className="text-sm text-muted-foreground">
            {preview?.source_wh_name
              ? `Со склада «${preview.source_wh_name}» — заказ на перемещение по каждому юрлицу`
              : "Заказ на перемещение по каждому юрлицу"}
          </p>
        </div>
      </div>

      {previewQ.isLoading || previewQ.error ? (
        <LoadState
          isLoading={previewQ.isLoading}
          error={previewQ.error}
          onRetry={() => void previewQ.refetch()}
          className="flex flex-col items-center justify-center gap-3 p-8 text-center"
        />
      ) : (
        <>
          {created.length > 0 && (
            <div className="rounded-lg border border-emerald-600/40 bg-emerald-50 p-3 text-sm dark:bg-emerald-950/30">
              <div className="mb-1 flex items-center gap-2 font-medium">
                <Check className="size-4" /> Заказы созданы
              </div>
              <ul className="space-y-0.5">
                {created.map((row) => (
                  <li key={row.number}>
                    {row.cabinet_name} — <b>{row.number}</b> ({row.lines} поз.)
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-muted-foreground">
                Выполняйте их в разделе «Проверка перемещений».
              </p>
              <Button
                size="sm"
                className="mt-2"
                onClick={() => navigate("/transfer-check")}
              >
                <Truck className="mr-1 size-4" /> К перемещениям
              </Button>
            </div>
          )}

          {/* Задание без опознанной номенклатуры в заказ не попадёт. Промолчать
            нельзя: заказ выйдет неполным, а вскроется это уже на сборке. */}
          {(preview?.unresolved_articles.length ?? 0) > 0 && (
            <div className="rounded-lg border border-amber-500/50 bg-amber-50 p-3 text-sm dark:bg-amber-950/30">
              <div className="mb-1 flex items-center gap-2 font-medium">
                <AlertTriangle className="size-4" /> Не опознана номенклатура 1С
              </div>
              <p>
                Эти артикулы в заказ не попадут —{" "}
                {preview?.unresolved_articles.join(", ")}. Нажмите «Обновить
                заказы» на экране ФБС; если не помогло, проверьте штрихкоды
                товара в 1С.
              </p>
            </div>
          )}

          {cabinets.length === 0 ? (
            <p className="rounded-lg border p-4 text-sm text-muted-foreground">
              Везти нечего: всё, что нужно по заданиям, уже лежит в комнате.
            </p>
          ) : (
            <div className="space-y-4">
              {/* ⚠️ Строка потребности — КОМНАТА, а не юрлицо: у кабинета с
                  двумя складами продавца их две, и документ у каждой свой.
                  Ключ поэтому по паре (юрлицо, склад) — иначе React считает
                  две комнаты одним элементом, а галка переключает обе. Сама
                  галка остаётся на ЮРЛИЦЕ: ручка создаёт документы по
                  `cabinet_ids`, и галка на комнате обещала бы выбор, которого
                  на сервере нет. */}
              {cabinets.map((cab) => (
                <section
                  key={`${keyOf(cab)}:${cab.wb_warehouse_id ?? "-"}`}
                  className="rounded-lg border"
                >
                  <header className="flex items-center gap-3 border-b p-3">
                    <input
                      type="checkbox"
                      className="size-5 accent-primary"
                      checked={!skipped.has(keyOf(cab))}
                      onChange={() => toggle(keyOf(cab))}
                      aria-label={`Заказывать для «${cab.cabinet_name}»`}
                    />
                    <div className="flex-1">
                      <div className="font-medium">
                        {cab.cabinet_name}
                        {cab.warehouse_name && (
                          <span className="ml-2 rounded bg-muted px-2 py-0.5 text-xs font-normal">
                            {cab.warehouse_name}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        нужно привезти {cab.total} {piecesWord(cab.total)}
                      </div>
                    </div>
                  </header>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-muted-foreground">
                        <tr className="border-b">
                          <th className="p-2 text-left font-normal">Товар</th>
                          <th className="p-2 text-right font-normal">
                            Не собрано
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {cab.rows.map((row) => (
                          <tr
                            key={`${row.nomenclature_ref}:${row.characteristic_ref}`}
                            className="border-b last:border-0"
                          >
                            <td className="p-2">
                              <div>{row.nomenclature_name || row.article}</div>
                              {row.characteristic_name && (
                                <div className="text-xs text-muted-foreground">
                                  {row.characteristic_name}
                                </div>
                              )}
                            </td>
                            <td className="p-2 text-right font-medium tabular-nums">
                              {row.needed}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ))}

              {createMut.isError && (
                <p className="text-sm text-destructive">
                  {apiErrorText(createMut.error, "Не удалось создать заказы")}
                </p>
              )}
              <Button
                className="h-12 w-full"
                disabled={chosen.length === 0 || createMut.isPending}
                onClick={() =>
                  createMut.mutate(
                    chosen.map((c) => ({
                      cabinet_id: c.cabinet_id,
                      marketplace: c.marketplace,
                    })),
                  )
                }
              >
                {createMut.isPending
                  ? "Создаём…"
                  : `Создать ${chosen.length} ${ordersWord(chosen.length)} на перемещение`}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
